import { useTranslations } from '@vocab/react';
import {
  Box,
  RadioGroup,
  RadioItem,
  Heading,
  Stack,
  Alert,
  Text,
} from 'braid-design-system';
import { observer } from 'mobx-react';
import * as React from 'react';

import { BULK_MOVE_LIMIT } from 'src/constants/numPerPage';
import { type Bucket, BUCKETS } from 'src/stores/JobApplicationStore/types';
import useBucketLabel from 'src/utils/hooks/useBucketLabel';
import useQueryParamsStore from 'src/utils/hooks/useQueryParamsStore';

import type { IJobApplicationStore } from '../../../../../stores/JobApplicationStore';
import ActionableForm, {
  type ActionableFormInputProps,
  type ActionResult,
  failResult,
  okResult,
} from '../../../../common/ActionableForm';
import { noop } from '../../../../type';
import translations from '../.vocab';
import type { BulkActionItemChildProps } from '../BulkActionItem';

interface FormValues extends Record<string, unknown> {
  bucket: string;
}

const UpdateStatusFormInput = observer(
  ({
    useFormField = noop,
  }: ActionableFormInputProps & {
    jobApplicationStore: IJobApplicationStore;
  }) => {
    const fieldName = 'bucket';
    const { t } = useTranslations(translations);
    const { getLabelFromBucket } = useBucketLabel();
    const bucket = useFormField({ id: fieldName });

    const validBuckets = Object.values(BUCKETS).filter(
      (b) => b !== BUCKETS.UNKNOWN,
    );
    return (
      <Box paddingY="small">
        <RadioGroup
          id="bulk-update-status-radio-group"
          onChange={bucket.onChange}
          value={bucket.value}
          label={t('Select status')}
        >
          {validBuckets.map((option) => (
            <RadioItem
              key={option}
              label={t('radio group bucket', {
                labelFromBucket: getLabelFromBucket(option),
              })}
              value={option}
            />
          ))}
        </RadioGroup>
      </Box>
    );
  },
);

interface FormProviderProps {
  jobApplicationStore: IJobApplicationStore;
  updateStatusForSelectedApplications: (bucket: Bucket) => Promise<boolean>;
  closeAndShowSuccessMessage: BulkActionItemChildProps['closeAndShowSuccessMessage'];
  onCancel: BulkActionItemChildProps['onCancel'];
  selectedJobApplicationsCount: BulkActionItemChildProps['selectedJobApplicationsCount'];
  enableModal: BulkActionItemChildProps['enableModal'];
  disableModal: BulkActionItemChildProps['disableModal'];
}

const UpdateStatusFormProvider = ({
  jobApplicationStore,
  updateStatusForSelectedApplications,
  closeAndShowSuccessMessage,
  onCancel,
  selectedJobApplicationsCount,
  enableModal,
  disableModal,
}: FormProviderProps) => {
  const { t } = useTranslations(translations);
  const { getLabelFromBucket } = useBucketLabel();
  const [, setQueryParams] = useQueryParamsStore();
  const originalBucket = jobApplicationStore.bucket;

  const onSubmit = async (formValues: any): Promise<ActionResult> => {
    const newBucket = formValues.bucket;

    const successResult = okResult(
      t('selectedJobApplicationsCount application/s moved to newBucket', {
        selectedJobApplicationsCount,
        newBucket: getLabelFromBucket(newBucket),
      }),
      undefined,
      {
        label: t('View'),
        onClick: () => {
          jobApplicationStore.clickViewMovedJobApplications();
          setQueryParams.navigateToBucket(newBucket);
        },
      },
    );

    if (originalBucket !== newBucket || jobApplicationStore.hasSearchText) {
      const result = await updateStatusForSelectedApplications(
        formValues.bucket as Bucket,
      );

      if (result) {
        return successResult;
      }

      return failResult(
        t('Sorry, we couldn’t save your changes. Can we try again?'),
      );
    }
    return failResult(
      t('Cannot move selected applications from originalBucket to newBucket.', {
        originalBucket: getLabelFromBucket(originalBucket),
        newBucket: getLabelFromBucket(newBucket),
      }),
    );
  };

  const initialFormValues: FormValues = { bucket: originalBucket };
  const selectedApplicationCount =
    jobApplicationStore.selectedJobApplications.length || 0;

  return (
    <Box>
      <Stack space="medium">
        <Heading level="4">{t('Move applications')}</Heading>
        {selectedApplicationCount > BULK_MOVE_LIMIT && (
          <Alert tone="caution">
            <Text>
              {t(
                "We can only move bulkMoveLimit applications at a time. You'll need to repeat these steps for the remaining applications.",
                {
                  bulkMoveLimit: BULK_MOVE_LIMIT,
                },
              )}
              <br />
              {t(
                "Moving many applications may take some time. Don't refresh or leave this page until we're done.",
              )}
            </Text>
          </Alert>
        )}
        <ActionableForm
          initialFormValues={initialFormValues}
          onCancel={() => {
            enableModal();
            onCancel();
          }}
          onSubmit={(formValue) => {
            disableModal();
            return onSubmit(formValue);
          }}
          onSubmitSuccess={(message, description, action) => {
            enableModal();
            closeAndShowSuccessMessage(message, description, action);
          }}
          onSubmitErrorWithSetErrorMessage={() => enableModal()}
          submitButtonLabel={t('Move')}
          submitButtonLoadingLabel={t('Moving')}
        >
          <UpdateStatusFormInput jobApplicationStore={jobApplicationStore} />
        </ActionableForm>
      </Stack>
    </Box>
  );
};

export default observer(UpdateStatusFormProvider);
